import React from "react";
import { useTranslation } from "react-i18next";
import Text from "@/components/ui/text";
import { getRelativeTime } from "@/utils/datetime";
import { TaskData } from "@/entities/tasks/types";
import { useUsers, useUsersMe } from "@/entities/users/hooks";
import CreatedByDreamerIndication from "@/components/controls/Task/CreatedByDreamerIndication";

const TRANSLATION_KEY = "tasks.createModal";

interface Props {
  taskData: TaskData;
}

const TaskMetadataSubtitle: React.FC<Props> = ({ taskData }) => {
  const { t } = useTranslation();
  const userData = useUsersMe().data;

  const { users: completedByUsers } = useUsers(
    taskData?.completedBy ? [taskData.completedBy] : []
  );
  const completedByData = completedByUsers?.[0] || null;

  const { users: createdByUsers } = useUsers(
    taskData?.createdBy ? [taskData.createdBy] : []
  );
  const createdByData = createdByUsers?.[0] || null;

  // Resolve assignee for the drill-down assignment-reason line. Only needed
  // when the context owner differs from the assignee.
  const needsAssignee =
    taskData?.isCreatedByDreamer &&
    taskData?.assigneeId &&
    taskData.createdBy !== taskData.assigneeId;
  const { users: assigneeUsers } = useUsers(
    needsAssignee && taskData.assigneeId ? [taskData.assigneeId] : []
  );
  const assigneeData = assigneeUsers?.[0] || null;

  const completedByName =
    completedByData?.id === userData?.id
      ? t("you")
      : completedByData?.displayName;

  const createdByName =
    createdByData?.id === userData?.id ? t("you") : createdByData?.displayName;

  const isCompleted = taskData?.isCompleted;
  const isCreatedByDreamer = taskData?.isCreatedByDreamer;

  const getTimestamp = () => {
    if (isCompleted && taskData.completedAt) {
      return getRelativeTime(new Date(taskData.completedAt));
    }
    if (taskData.createdAt) {
      return getRelativeTime(new Date(taskData.createdAt));
    }
    return null;
  };

  const timestamp = getTimestamp();

  // Open + Dreamer-created: render the same clickable chip used in the task
  // list so the drill-down is reachable from the right panel. This branch
  // runs before the !timestamp guard because the chip doesn't need createdAt.
  if (isCreatedByDreamer && !isCompleted) {
    const contextOwnerName = taskData.createdBy
      ? createdByData?.displayName ?? null
      : null;
    const assigneeName =
      assigneeData?.id === userData?.id
        ? t("you")
        : assigneeData?.displayName ?? null;

    return (
      <div className="flex gap-1 items-center">
        <CreatedByDreamerIndication
          task={taskData}
          contextOwnerName={contextOwnerName}
          contextOwnerUserData={createdByData ?? null}
          assigneeName={assigneeName}
        />
      </div>
    );
  }

  if (!timestamp) {
    return null;
  }

  const displayName = isCompleted
    ? completedByName || (isCreatedByDreamer ? t("dreamer") : null)
    : createdByName;
  const actionKey = isCompleted ? "completed" : "created";

  return (
    <div className="flex gap-1 items-center">
      <Text variant="bodySecondary">
        {`${t(`${TRANSLATION_KEY}.${actionKey}`)}${displayName ? ` ${t(`${TRANSLATION_KEY}.by`)} ${displayName}` : ""} ${timestamp}`}
      </Text>
    </div>
  );
};

export default TaskMetadataSubtitle;
